import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import { userProfileCache } from './referenceDataCache';
import type { Notification } from '../types';
import { logger } from '../utils/logger';

interface NotificationStore {
  notifications: Notification[];
  whatsappNotifications: Notification[];
  unreadCount: number;
  whatsappUnreadCount: number;
  loading: boolean;
  error: string | null;
  realtimeConnected: boolean;
  // Selezione multipla
  selectedIds: Set<string>;
  // Metodi esistenti
  fetchNotifications: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  markAllWhatsAppAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  deleteAllReadNotifications: () => Promise<void>;
  createNotification: (notification: Omit<Notification, 'id' | 'created_at' | 'read'>) => Promise<void>;
  handleWhatsAppMessage: (message: any) => Promise<void>;
  getWhatsAppNotificationsByChat: (chatId: string) => Notification[];
  getNotificationsByFilter: (filter: 'all' | 'unread' | 'read') => Notification[];
  getNotificationsGroupedByDate: () => { [key: string]: Notification[] };
  // Nuovi metodi per selezione multipla e bulk actions
  toggleSelection: (id: string) => void;
  selectAll: () => void;
  clearSelection: () => void;
  bulkMarkAsRead: (ids: string[]) => Promise<void>;
  bulkDelete: (ids: string[]) => Promise<void>;
  getNotificationsByCategory: () => { [key: string]: Notification[] };
}

export const useNotificationStore = create<NotificationStore>((set, get) => ({
  notifications: [],
  whatsappNotifications: [],
  unreadCount: 0,
  whatsappUnreadCount: 0,
  loading: false,
  error: null,
  realtimeConnected: false,
  selectedIds: new Set<string>(),

  fetchNotifications: async () => {
    // Due componenti montano lo store al load: evita la doppia fetch
    if (get().loading) return;
    set({ loading: true, error: null });
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        console.error("[NotificationStore] ❌ Utente non autenticato durante il caricamento notifiche");
        throw new Error('User not authenticated');
      }

      // Recupera l'organization_id dalla cache condivisa (dedup dei lookup)
      const organizationId = await userProfileCache.getOrganizationId(userData.user.id).catch(() => null);

      if (!organizationId) {
        console.error("[NotificationStore] ❌ Utente non associato a un'organizzazione");
        throw new Error('User not associated with an organization');
      }

      logger.log('notification', `Caricamento notifiche per utente: ${userData.user.id.substring(0, 8)}... org: ${organizationId.substring(0, 8)}...`);

      // Recupera tutte le notifiche per l'utente nella sua organizzazione
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userData.user.id)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error("[NotificationStore] ❌ Errore query notifiche:", error);
        throw error;
      }
      
      const allNotifications = data || [];
      
      // Separa le notifiche WhatsApp dalle notifiche normali
      const whatsappNotifications = allNotifications.filter(n => n.whatsapp_chat_id);
      const regularNotifications = allNotifications.filter(n => !n.whatsapp_chat_id);
      
      logger.log('notification', `Trovate ${allNotifications.length} notifiche (${whatsappNotifications.length} WhatsApp, ${regularNotifications.length} standard)`);
      
      // Debug dettagliato solo se necessario
      if (whatsappNotifications.length > 0 && logger.getRemainingLogs('notification') > 0) {
        logger.log('notification', `Prime notifiche WhatsApp disponibili: ${whatsappNotifications.slice(0, 2).map(n => n.title).join(', ')}`);
      }
      
      // Calcola i conteggi dei non letti
      const unreadCount = regularNotifications.filter(n => !n.read).length;
      const filteredWhatsappNotifications = whatsappNotifications;
      
      const filteredWhatsappUnreadCount = filteredWhatsappNotifications.filter(n => !n.read).length;
      
      set({ 
        notifications: regularNotifications, 
        whatsappNotifications: filteredWhatsappNotifications,
        unreadCount,
        whatsappUnreadCount: filteredWhatsappUnreadCount,
        loading: false 
      });
    } catch (error) {
      console.error("[NotificationStore] ❌ Errore caricamento notifiche:", error);
      set({ error: friendlyDbError(error), loading: false });
    }
  },

  markAsRead: async (id) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', id);

      if (error) throw error;
      
      const notification = [...get().notifications, ...get().whatsappNotifications].find(n => n.id === id);
      
      if (notification?.whatsapp_chat_id) {
        console.log('[REALTIME] Marcando notifica WhatsApp come letta');
        // È una notifica WhatsApp
        const whatsappNotifications = get().whatsappNotifications.map(n => 
          n.id === id ? { ...n, read: true } : n
        );
        
        const whatsappUnreadCount = whatsappNotifications.filter(n => !n.read).length;
        console.log(`[REALTIME] Nuovo conteggio WhatsApp: ${whatsappUnreadCount}`);
        
        set({ whatsappNotifications, whatsappUnreadCount });
      } else {
        // È una notifica standard
        const notifications = get().notifications.map(n => 
          n.id === id ? { ...n, read: true } : n
        );
        
        const unreadCount = notifications.filter(n => !n.read).length;
        console.log(`[NotificationStore] 📩 Nuovo conteggio standard: ${unreadCount}`);
        
        set({ notifications, unreadCount });
      }
    } catch (error) {
      console.error('[NotificationStore] ❌ Errore marcando notifica come letta:', error);
      set({ error: friendlyDbError(error) });
    }
  },

  markAllAsRead: async () => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      // Recupera organization_id dell'utente
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      // Marca come lette solo le notifiche standard (non WhatsApp) della stessa organizzazione
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', userData.user.id)
        .eq('organization_id', userProfile.organization_id)
        .eq('read', false)
        .is('whatsapp_chat_id', null);

      if (error) throw error;

      // Aggiorna lo stato locale
      const notifications = get().notifications.map(n => ({ ...n, read: true }));
      
      set({ notifications, unreadCount: 0 });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    }
  },
  
  markAllWhatsAppAsRead: async () => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      // Recupera organization_id dell'utente
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      console.log('[REALTIME] Marcando tutte le notifiche WhatsApp come lette');

      // Marca come lette tutte le notifiche WhatsApp della stessa organizzazione
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', userData.user.id)
        .eq('organization_id', userProfile.organization_id)
        .eq('read', false)
        .not('whatsapp_chat_id', 'is', null);

      if (error) throw error;

      // Aggiorna lo stato locale
      const whatsappNotifications = get().whatsappNotifications.map(n => ({ ...n, read: true }));
      
      console.log('[REALTIME] Tutte le notifiche WhatsApp marcate come lette');
      set({ whatsappNotifications, whatsappUnreadCount: 0 });
    } catch (error) {
      console.error('[REALTIME] Errore marcando tutte le notifiche WhatsApp come lette:', error);
      set({ error: friendlyDbError(error) });
    }
  },

  deleteNotification: async (id) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', id);

      if (error) throw error;

      const notification = [...get().notifications, ...get().whatsappNotifications].find(n => n.id === id);
      console.log(`[NotificationStore] 🗑️ Eliminando notifica:`, notification);
      
      // Determina se è una notifica WhatsApp o standard
      if (notification?.whatsapp_chat_id) {
        console.log('[REALTIME] Eliminazione notifica WhatsApp');
        // Notifica WhatsApp
        const whatsappNotifications = get().whatsappNotifications.filter(n => n.id !== id);
        const whatsappUnreadCount = whatsappNotifications.filter(n => !n.read).length;
        console.log(`[REALTIME] Nuovo conteggio WhatsApp dopo eliminazione: ${whatsappUnreadCount}`);
        set({ whatsappNotifications, whatsappUnreadCount });
      } else {
        // Notifica standard
        const notifications = get().notifications.filter(n => n.id !== id);
        const unreadCount = notifications.filter(n => !n.read).length;
        console.log(`[NotificationStore] 📩 Nuovo conteggio standard dopo eliminazione: ${unreadCount}`);
        set({ notifications, unreadCount });
      }
    } catch (error) {
      console.error('[NotificationStore] ❌ Errore eliminando notifica:', error);
      set({ error: friendlyDbError(error) });
    }
  },

  createNotification: async (notification) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      // RLS richiede organization_id: usa quello passato dal chiamante o fallback al profilo dell'utente
      let organizationId = (notification as { organization_id?: string }).organization_id;
      if (!organizationId) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('organization_id')
          .eq('id', userData.user.id)
          .single();
        organizationId = profile?.organization_id;
      }

      const { data, error } = await supabase
        .from('notifications')
        .insert({
          ...notification,
          organization_id: organizationId,
          user_id: userData.user.id,
          read: false,
        })
        .select()
        .single();

      if (error) throw error;

      console.log('[NotificationStore] 📤 Creata nuova notifica:', data);

      // Determina se è una notifica di WhatsApp in base alla presenza di whatsapp_chat_id
      if (data.whatsapp_chat_id) {
        console.log('[REALTIME] Creazione nuova notifica WhatsApp');
        // Aggiorna lo store delle notifiche WhatsApp
        const currentWhatsAppCount = typeof get().whatsappUnreadCount === 'number' ? get().whatsappUnreadCount : 0;
        const newWhatsAppCount = currentWhatsAppCount + 1;
        
        set({ 
          whatsappNotifications: [data, ...get().whatsappNotifications],
          whatsappUnreadCount: newWhatsAppCount
        });
        
        console.log('[REALTIME] Aggiornate notifiche WhatsApp, nuovo totale:', newWhatsAppCount);

        // Ricarica tutte le notifiche per assicurarsi che i filtri di permesso siano applicati
        setTimeout(() => get().fetchNotifications(), 500);
      } else {
        // Aggiorna lo store delle notifiche standard
        const currentCount = typeof get().unreadCount === 'number' ? get().unreadCount : 0;
        const newCount = currentCount + 1;
        
        set({ 
          notifications: [data, ...get().notifications],
          unreadCount: newCount
        });
        
        console.log('[NotificationStore] 📩 Aggiornate notifiche standard, nuovo totale:', newCount);
      }
    } catch (error) {
      console.error('[NotificationStore] ❌ Errore nella creazione notifica:', error);
      set({ error: friendlyDbError(error) });
    }
  },

  /**
   * Create a notification for an invoice queue item
   * @param queueItem The queue item to create a notification for
   * @param type The type of notification (due, processed, error)
   */
  createInvoiceQueueNotification: async (queueItem: any, type: 'due' | 'processed' | 'error') => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');
      
      // Get user to be notified (owner of the queue item or assigned user)
      let userId = userData.user.id;
      
      // If the queue item has a deal with an assigned user, use that instead
      if (queueItem.deal?.assigned_to) {
        userId = queueItem.deal.assigned_to;
      }
      
      // Determine notification content based on type
      let title, body, icon, data;
      let eventType: string;

      switch (type) {
        case 'due':
          title = 'Fattura in scadenza';
          body = `Fattura di ${queueItem.amount.toFixed(2)}€ per ${queueItem.deal?.client?.business_name || 'cliente'} in scadenza oggi`;
          icon = 'clock';
          data = { queue_item_id: queueItem.id, action: 'process', due_date: queueItem.due_date };
          eventType = 'invoice_due_soon';
          break;
        case 'processed':
          title = 'Fattura elaborata con successo';
          body = `La fattura per ${queueItem.deal?.client?.business_name || 'cliente'} è stata elaborata con successo`;
          icon = 'check-circle';
          data = { queue_item_id: queueItem.id, invoice_id: queueItem.invoice_id };
          eventType = 'invoice_paid';
          break;
        case 'error':
          title = 'Errore elaborazione fattura';
          body = `Si è verificato un errore durante l'elaborazione della fattura per ${queueItem.deal?.client?.business_name || 'cliente'}`;
          icon = 'alert-triangle';
          data = { queue_item_id: queueItem.id, action: 'retry' };
          eventType = 'invoice_overdue';
          break;
      }

      // RLS richiede organization_id: prendi dall'org del destinatario (userId)
      const { data: recipientProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userId)
        .single();

      // Create the notification
      const { data: notification, error } = await supabase
        .from('notifications')
        .insert({
          organization_id: recipientProfile?.organization_id,
          user_id: userId,
          event_type: eventType!,
          title,
          body,
          icon,
          type: 'invoice_queue',
          data,
          read: false
        })
        .select()
        .single();

      if (error) throw error;

      console.log(`[NotificationStore] 📤 Creata nuova notifica di fatturazione:`, notification);
      
      // Update the local state if this notification is for the current user
      if (userId === userData.user.id) {
        // Update notifications list and unread count
        const currentCount = typeof get().unreadCount === 'number' ? get().unreadCount : 0;
        const newCount = currentCount + 1;
        
        set({ 
          notifications: [notification, ...get().notifications],
          unreadCount: newCount
        });
      }

      return notification;
    } catch (error) {
      console.error('Error creating invoice queue notification:', error);
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  handleWhatsAppMessage: async (message) => {
    try {
      // ❌ NOTIFICHE WHATSAPP DISABILITATE
      // Le notifiche WhatsApp nel centro notifiche sono state disabilitate
      // Il realtime aggiorna automaticamente l'UI quando arrivano nuovi messaggi
      console.log('[REALTIME] Messaggio WhatsApp ricevuto (notifiche disabilitate)');

      // La funzione viene mantenuta per compatibilità ma non crea più notifiche
      return;

      /* CODICE NOTIFICHE DISABILITATO
      console.log('[REALTIME] Elaborazione messaggio WhatsApp in notificationStore');

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        console.error('[REALTIME] Utente non autenticato durante elaborazione notifica');
        throw new Error('User not authenticated');
      }

      // Dobbiamo gestire sia i messaggi dal webhook che quelli dalla realtime API
      let phoneNumber, chatId, messageId, configId, messageText, fromMe;

      // Verifica se il messaggio ha una struttura da webhook o da realtime
      if (message.payload) {
        // Formato da webhook
        phoneNumber = message.payload.from?.split('@')[0];
        chatId = message.payload.from;
        messageId = message.payload.id;
        configId = message.config_id || null;
        messageText = message.payload.body || '[Media Message]';
        fromMe = message.payload.fromMe || false;
      } else if (message.from_jid || message.from) {
        // Formato da realtime API (tabella whatsapp_messages)
        phoneNumber = (message.from_jid || message.from || '').split('@')[0];
        chatId = message.from_jid || message.from;
        messageId = message.id;
        configId = message.config_id || null;
        messageText = message.content || '[Media Message]';
        fromMe = message.from_me || message.fromMe || message.direction === 'outbound' || false;
      } else {
        console.error('[REALTIME] Formato messaggio non riconosciuto durante elaborazione notifica');
        return;
      }

      // Verifica che abbiamo estratto i dati necessari
      if (!phoneNumber || !chatId) {
        console.error('[REALTIME] Dati essenziali mancanti per notifica WhatsApp');
        return;
      }

      console.log('[REALTIME] Messaggio WhatsApp estratto per notifica', {
        direzione: fromMe ? 'OUT' : 'IN',
        chat: chatId?.substring(0, 8)
      });

      // Se il messaggio è inviato da noi, non creare notifica
      if (fromMe) {
        console.log('[REALTIME] Ignorato messaggio in uscita per notifica');
        return;
      }

      // Trova il cliente tramite il numero di telefono - usando ilike nel modo corretto
      const { data: clients, error: clientError } = await supabase
        .from('clients')
        .select('id, business_name, assigned_to')
        .ilike('phone', `%${phoneNumber}%`)
        .single();

      if (clientError || !clients) {
        console.log(`[REALTIME] Nessun cliente trovato per il numero ${phoneNumber}, provo con whatsapp_number`);

        // Secondo tentativo con whatsapp_number
        const { data: whatsappClients, error: whatsappClientError } = await supabase
          .from('clients')
          .select('id, business_name, assigned_to')
          .ilike('whatsapp_number', `%${phoneNumber}%`)
          .single();

        if (whatsappClientError || !whatsappClients) {
          console.log(`[REALTIME] Cliente non trovato neanche con whatsapp_number per ${phoneNumber}`);
          return;
        }

        console.log('[REALTIME] Cliente trovato con whatsapp_number, creazione notifica');

        // Crea la notifica per l'utente assegnato
        if (whatsappClients.assigned_to) {
          // Creiamo direttamente la notifica
          await get().createNotification({
            user_id: whatsappClients.assigned_to,
            event_type: 'whatsapp_message_received',
            type: 'info',
            title: 'Nuovo messaggio WhatsApp',
            message: `Messaggio da ${whatsappClients.business_name}: "${messageText.substring(0, 50)}${messageText.length > 50 ? '...' : ''}"`,
            link: `/whatsapp?chat=${chatId}&config=${configId}`,
            config_id: configId,
            whatsapp_chat_id: chatId,
            whatsapp_phone: phoneNumber,
            whatsapp_message_id: messageId,
            entity_type: 'client',
            entity_id: whatsappClients.id
          });

          // Forziamo un refresh per assicurarci che il contatore si aggiorni
          console.log('[REALTIME] Aggiornamento notifiche schedulato');
          setTimeout(() => get().fetchNotifications(), 1000);
        } else {
          console.log('[REALTIME] Cliente senza utente assegnato, notifica non creata');
        }

        return;
      }

      console.log('[REALTIME] Cliente trovato con phone, creazione notifica');

      // Crea la notifica per l'utente assegnato con i nuovi campi WhatsApp
      if (clients.assigned_to) {
        await get().createNotification({
          user_id: clients.assigned_to,
          event_type: 'whatsapp_message_received',
          type: 'info',
          title: 'Nuovo messaggio WhatsApp',
          message: `Messaggio da ${clients.business_name}: "${messageText.substring(0, 50)}${messageText.length > 50 ? '...' : ''}"`,
          link: `/whatsapp?chat=${chatId}&config=${configId}`,
          config_id: configId,
          whatsapp_chat_id: chatId,
          whatsapp_phone: phoneNumber,
          whatsapp_message_id: messageId,
          entity_type: 'client',
          entity_id: clients.id
        });

        // Forziamo un refresh per assicurarci che il contatore si aggiorni
        console.log('[REALTIME] Aggiornamento notifiche schedulato');
        setTimeout(() => get().fetchNotifications(), 1000);
      } else {
        console.log('[REALTIME] Cliente senza utente assegnato, notifica non creata');
      }
      */
    } catch (error) {
      console.error('[REALTIME] Errore nel processare la notifica WhatsApp:', error);
    }
  },
  
  // Ottiene le notifiche WhatsApp per una specifica chat
  getWhatsAppNotificationsByChat: (chatId: string) => {
    // Filtra le notifiche per la chat specifica
    return get().whatsappNotifications.filter(n => n.whatsapp_chat_id === chatId);
  },

  // Elimina tutte le notifiche lette
  deleteAllReadNotifications: async () => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      // Recupera organization_id dell'utente
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      // Elimina tutte le notifiche lette (incluse WhatsApp) della stessa organizzazione
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('user_id', userData.user.id)
        .eq('organization_id', userProfile.organization_id)
        .eq('read', true);

      if (error) throw error;

      // Aggiorna lo stato locale rimuovendo le notifiche lette (sia standard che WhatsApp)
      const notifications = get().notifications.filter(n => !n.read);
      const whatsappNotifications = get().whatsappNotifications.filter(n => !n.read);

      console.log('[NotificationStore] 🗑️ Eliminate tutte le notifiche lette');
      set({ notifications, whatsappNotifications });
    } catch (error) {
      console.error('[NotificationStore] ❌ Errore eliminando notifiche lette:', error);
      set({ error: friendlyDbError(error) });
    }
  },

  // Filtra le notifiche per stato (all, unread, read)
  getNotificationsByFilter: (filter: 'all' | 'unread' | 'read') => {
    const allNotifications = [...get().notifications, ...get().whatsappNotifications];

    switch (filter) {
      case 'unread':
        return allNotifications.filter(n => !n.read);
      case 'read':
        return allNotifications.filter(n => n.read);
      case 'all':
      default:
        return allNotifications;
    }
  },

  // Raggruppa le notifiche per data
  getNotificationsGroupedByDate: () => {
    const allNotifications = [...get().notifications, ...get().whatsappNotifications];

    // Ordina per data (più recente prima)
    const sorted = [...allNotifications].sort((a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const thisWeekStart = new Date(today);
    thisWeekStart.setDate(thisWeekStart.getDate() - 7);

    const groups: { [key: string]: Notification[] } = {
      'Oggi': [],
      'Ieri': [],
      'Questa settimana': [],
      'Più vecchie': []
    };

    sorted.forEach(notification => {
      const notificationDate = new Date(notification.created_at);
      notificationDate.setHours(0, 0, 0, 0);

      if (notificationDate.getTime() === today.getTime()) {
        groups['Oggi'].push(notification);
      } else if (notificationDate.getTime() === yesterday.getTime()) {
        groups['Ieri'].push(notification);
      } else if (notificationDate.getTime() >= thisWeekStart.getTime()) {
        groups['Questa settimana'].push(notification);
      } else {
        groups['Più vecchie'].push(notification);
      }
    });

    // Rimuovi gruppi vuoti
    Object.keys(groups).forEach(key => {
      if (groups[key].length === 0) {
        delete groups[key];
      }
    });

    return groups;
  },

  // ================ NUOVI METODI PER SELEZIONE MULTIPLA E BULK ACTIONS ================

  toggleSelection: (id: string) => {
    const newSelected = new Set(get().selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    set({ selectedIds: newSelected });
  },

  selectAll: () => {
    const allIds = [...get().notifications, ...get().whatsappNotifications].map(n => n.id);
    set({ selectedIds: new Set(allIds) });
  },

  clearSelection: () => {
    set({ selectedIds: new Set() });
  },

  bulkMarkAsRead: async (ids: string[]) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      // Aggiorna nel database
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .in('id', ids);

      if (error) throw error;

      // Aggiorna stato locale
      const notifications = get().notifications.map(n =>
        ids.includes(n.id) ? { ...n, read: true } : n
      );
      const whatsappNotifications = get().whatsappNotifications.map(n =>
        ids.includes(n.id) ? { ...n, read: true } : n
      );

      const unreadCount = notifications.filter(n => !n.read).length;
      const whatsappUnreadCount = whatsappNotifications.filter(n => !n.read).length;

      set({
        notifications,
        whatsappNotifications,
        unreadCount,
        whatsappUnreadCount,
        selectedIds: new Set() // Deseleziona dopo l'azione
      });

      console.log(`[NotificationStore] ✅ Marcate ${ids.length} notifiche come lette`);
    } catch (error) {
      console.error('[NotificationStore] ❌ Errore bulk mark as read:', error);
      set({ error: friendlyDbError(error) });
    }
  },

  bulkDelete: async (ids: string[]) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      // Elimina dal database
      const { error } = await supabase
        .from('notifications')
        .delete()
        .in('id', ids);

      if (error) throw error;

      // Aggiorna stato locale
      const notifications = get().notifications.filter(n => !ids.includes(n.id));
      const whatsappNotifications = get().whatsappNotifications.filter(n => !ids.includes(n.id));

      const unreadCount = notifications.filter(n => !n.read).length;
      const whatsappUnreadCount = whatsappNotifications.filter(n => !n.read).length;

      set({
        notifications,
        whatsappNotifications,
        unreadCount,
        whatsappUnreadCount,
        selectedIds: new Set() // Deseleziona dopo l'azione
      });

      console.log(`[NotificationStore] 🗑️ Eliminate ${ids.length} notifiche`);
    } catch (error) {
      console.error('[NotificationStore] ❌ Errore bulk delete:', error);
      set({ error: friendlyDbError(error) });
    }
  },

  // Raggruppa le notifiche per categoria
  getNotificationsByCategory: () => {
    const allNotifications = [...get().notifications, ...get().whatsappNotifications];

    // Ordina per data (più recente prima)
    const sorted = [...allNotifications].sort((a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );

    const groups: { [key: string]: Notification[] } = {
      'Task': [],
      'Lead': [],
      'Deal': [],
      'Cliente': [],
      'Progetto': [],
      'WhatsApp': [],
      'Altro': []
    };

    sorted.forEach(notification => {
      const eventType = notification.event_type || '';
      const isWhatsApp = !!notification.whatsapp_chat_id;

      if (isWhatsApp) {
        groups['WhatsApp'].push(notification);
      } else if (eventType.startsWith('task_') || eventType === 'mention') {
        groups['Task'].push(notification);
      } else if (eventType.startsWith('lead_')) {
        groups['Lead'].push(notification);
      } else if (eventType.startsWith('deal_')) {
        groups['Deal'].push(notification);
      } else if (eventType.startsWith('client_')) {
        groups['Cliente'].push(notification);
      } else if (eventType.startsWith('project_')) {
        groups['Progetto'].push(notification);
      } else {
        groups['Altro'].push(notification);
      }
    });

    // Rimuovi gruppi vuoti
    Object.keys(groups).forEach(key => {
      if (groups[key].length === 0) {
        delete groups[key];
      }
    });

    return groups;
  }
}));